import React, { useState, useEffect, useRef, useCallback } from "react";
import { 
  getFirestore, collection, query, orderBy, onSnapshot, 
  getDoc, doc, getDocs, where, setDoc, addDoc, serverTimestamp, limit, writeBatch, updateDoc, arrayRemove 
} from "firebase/firestore";
import { getStorage, ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { useSearchParams } from "react-router-dom";
import { FaMicrophone, FaPaperPlane, FaPaperclip, FaStopCircle, FaImage, FaFile, FaTimes, FaCircleNotch, FaInfoCircle, FaArrowLeft, FaComments, FaBell, FaDownload, FaCog, FaCheck, FaCheckDouble, FaSearch } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { Tabs, Tab } from "react-bootstrap";
import {
  getChatNotificationPermission,
  getChatNotificationsEnabled,
  requestChatNotificationPermission,
  setChatNotificationsEnabled,
} from "../../services/chatNotifications";
import {
  getPwaInstallState,
  promptPwaInstallation,
  subscribeToPwaInstallState,
} from "../../services/pwaInstall";
import {
  DOCUMENT_ACCEPT,
  getFileExtension,
  sanitizeChatFileName,
  validateChatAttachment,
} from "../../utils/chatAttachments";
import ChatMessageText from "./ChatMessageText";
import "./ChatView.css";

const MAX_FILE_SIZE = 1024 * 1024; // 1MB en bytes
const CHAT_PRESENCE_TIMEOUT_MS = 2 * 60 * 1000;

const timestampToDate = (timestamp) => {
  if (!timestamp) return null;
  if (typeof timestamp.toDate === "function") return timestamp.toDate();
  if (timestamp instanceof Date) return timestamp;
  const parsedDate = new Date(timestamp);
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const timestampToMillis = (timestamp) => timestampToDate(timestamp)?.getTime() || 0;

const isSameCalendarDay = (firstTimestamp, secondTimestamp) => {
  const firstDate = timestampToDate(firstTimestamp);
  const secondDate = timestampToDate(secondTimestamp);
  return Boolean(
    firstDate
    && secondDate
    && firstDate.getFullYear() === secondDate.getFullYear()
    && firstDate.getMonth() === secondDate.getMonth()
    && firstDate.getDate() === secondDate.getDate()
  );
};

const formatMessageDate = (timestamp) => {
  const date = timestampToDate(timestamp);
  if (!date) return "Sin fecha";

  const today = new Date();
  if (isSameCalendarDay(date, today)) return "Hoy";

  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  if (isSameCalendarDay(date, yesterday)) return "Ayer";

  return new Intl.DateTimeFormat("es-CO", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: date.getFullYear() === today.getFullYear() ? undefined : "numeric",
  }).format(date);
};

const formatConversationTime = (timestamp) => {
  const date = timestampToDate(timestamp);
  if (!date) return "";
  const today = new Date();
  if (isSameCalendarDay(date, today)) {
    return new Intl.DateTimeFormat("es-CO", { hour: "2-digit", minute: "2-digit" }).format(date);
  }
  if (date.getFullYear() === today.getFullYear()) {
    return new Intl.DateTimeFormat("es-CO", { day: "2-digit", month: "short" }).format(date);
  }
  return new Intl.DateTimeFormat("es-CO", { day: "2-digit", month: "short", year: "2-digit" }).format(date);
};

const formatLastConnection = (presence) => {
  const lastSeenDate = timestampToDate(presence?.lastSeenAt);
  if (!lastSeenDate) return "Sin conexión reciente";

  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - lastSeenDate.getTime()) / 60000));
  const isOnline = presence?.online === true && Date.now() - lastSeenDate.getTime() < CHAT_PRESENCE_TIMEOUT_MS;
  if (isOnline) return "En línea";
  if (elapsedMinutes < 1) return "Última conexión hace un momento";
  if (elapsedMinutes < 60) return `Última conexión hace ${elapsedMinutes} min`;

  const today = new Date();
  const isToday = lastSeenDate.toDateString() === today.toDateString();
  const time = new Intl.DateTimeFormat("es-CO", { hour: "2-digit", minute: "2-digit" }).format(lastSeenDate);
  if (isToday) return `Última conexión hoy, ${time}`;

  return `Última conexión ${new Intl.DateTimeFormat("es-CO", { day: "2-digit", month: "short" }).format(lastSeenDate)}, ${time}`;
};

const ChatView = ({ closeNavbar }) => {
  const [contacts, setContacts] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [userRole, setUserRole] = useState({ isMaster: false, teacher: false, teacherId: null, uid: null, isStudent: false, displayName: "" });
  const [activeContactTab, setActiveContactTab] = useState("all");
  const [contactSearch, setContactSearch] = useState("");
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [recording, setRecording] = useState(false);
  const [audioDuration, setAudioDuration] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileError, setFileError] = useState("");
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const [adminConversations, setAdminConversations] = useState(new Set());
  const [showUserInfo, setShowUserInfo] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState({});
  const [isMobileView, setIsMobileView] = useState(false);
  const [showChatList, setShowChatList] = useState(true);
  const [openedConversationId, setOpenedConversationId] = useState(null);
  const [hasNewMessages, setHasNewMessages] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState(
    getChatNotificationPermission
  );
  const [notificationsEnabled, setNotificationsEnabled] = useState(
    getChatNotificationsEnabled
  );
  const [showChatSettings, setShowChatSettings] = useState(false);
  const [pwaInstallState, setPwaInstallState] = useState(getPwaInstallState);
  const [isRequestingNotifications, setIsRequestingNotifications] = useState(false);
  const [isInstallingPwa, setIsInstallingPwa] = useState(false);
  const [enablementDismissed, setEnablementDismissed] = useState(
    () => window.sessionStorage.getItem("chat-enablement-dismissed") === "true"
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const chatMessagesRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const attachmentKindRef = useRef("document");
  const isNearBottomRef = useRef(true);
  const previousMessageCountRef = useRef(0);
  
  const db = getFirestore();
  const auth = getAuth();
  const storage = getStorage();
  const selectedConversationId = searchParams.get("conversationId");

  // Cierra el navbar al cargar el componente
  useEffect(() => {
    if (closeNavbar) closeNavbar();
  }, [closeNavbar]);

  useEffect(
    () => subscribeToPwaInstallState(setPwaInstallState),
    []
  );

  const enableNotifications = async () => {
    try {
      setIsRequestingNotifications(true);
      const permission =
        notificationPermission === "granted"
          ? "granted"
          : await requestChatNotificationPermission();
      setNotificationPermission(permission);

      if (permission === "granted") {
        setChatNotificationsEnabled(true);
        setNotificationsEnabled(true);
      }
    } catch (error) {
      console.error("No fue posible habilitar las notificaciones:", error);
      setFileError("No fue posible habilitar las notificaciones en este navegador.");
    } finally {
      setIsRequestingNotifications(false);
    }
  };

  const installPwa = async () => {
    if (!pwaInstallState.promptAvailable || isInstallingPwa) return;

    try {
      setIsInstallingPwa(true);
      await promptPwaInstallation();
    } catch (error) {
      console.error("No fue posible instalar la PWA:", error);
      setFileError("No fue posible iniciar la instalación en este navegador.");
    } finally {
      setIsInstallingPwa(false);
    }
  };

  const dismissEnablementPrompt = () => {
    window.sessionStorage.setItem("chat-enablement-dismissed", "true");
    setEnablementDismissed(true);
  };

  const openChatSettings = () => {
    window.sessionStorage.setItem("chat-enablement-dismissed", "true");
    setEnablementDismissed(true);
    setShowUserInfo(false);
    setShowChatSettings(true);
  };

  const handleNotificationsPreference = async () => {
    if (notificationsEnabled && notificationPermission === "granted") {
      setChatNotificationsEnabled(false);
      setNotificationsEnabled(false);
      return;
    }

    await enableNotifications();
  };

  // Mantener el chat dentro del viewport visible, incluso con teclado virtual.
  useEffect(() => {
    const mobileQuery = window.matchMedia("(max-width: 1024px)");
    const visualViewport = window.visualViewport;

    const updateViewport = () => {
      const viewportHeight = mobileQuery.matches
        ? visualViewport?.height || window.innerHeight
        : window.innerHeight;
      const keyboardOpen = Boolean(
        mobileQuery.matches &&
        visualViewport &&
        window.innerHeight - visualViewport.height > 140
      );

      document.documentElement.style.setProperty(
        "--chat-visual-height",
        `${Math.round(viewportHeight)}px`
      );
      document.body.classList.toggle("chat-keyboard-open", keyboardOpen);
    };

    const updateLayoutMode = (event) => {
      const isMobile = event.matches;
      setIsMobileView(isMobile);
      if (!isMobile) {
        setShowChatList(true);
      }
    };

    updateLayoutMode(mobileQuery);
    updateViewport();
    mobileQuery.addEventListener("change", updateLayoutMode);
    window.addEventListener("resize", updateViewport);
    visualViewport?.addEventListener("resize", updateViewport);
    visualViewport?.addEventListener("scroll", updateViewport);

    return () => {
      mobileQuery.removeEventListener("change", updateLayoutMode);
      window.removeEventListener("resize", updateViewport);
      visualViewport?.removeEventListener("resize", updateViewport);
      visualViewport?.removeEventListener("scroll", updateViewport);
      document.documentElement.style.removeProperty("--chat-visual-height");
      document.body.classList.remove("chat-keyboard-open");
    };
  }, []);

  const scrollToLatestMessage = useCallback((behavior = "smooth") => {
    const messagesElement = chatMessagesRef.current;
    if (!messagesElement) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    messagesElement.scrollTo({
      top: messagesElement.scrollHeight,
      behavior: prefersReducedMotion ? "auto" : behavior,
    });
    isNearBottomRef.current = true;
    setHasNewMessages(false);
  }, []);

  const handleMessagesScroll = useCallback(() => {
    const messagesElement = chatMessagesRef.current;
    if (!messagesElement) return;

    const distanceFromBottom =
      messagesElement.scrollHeight - messagesElement.scrollTop - messagesElement.clientHeight;
    const isNearBottom = distanceFromBottom < 96;
    isNearBottomRef.current = isNearBottom;

    if (isNearBottom) {
      setHasNewMessages(false);
    }
  }, []);

  // Seguir mensajes nuevos solo cuando el usuario ya está cerca del final.
  useEffect(() => {
    const previousCount = previousMessageCountRef.current;
    const receivedNewMessages = messages.length > previousCount;

    if (previousCount === 0 || isNearBottomRef.current) {
      requestAnimationFrame(() => {
        scrollToLatestMessage(previousCount === 0 ? "auto" : "smooth");
      });
    } else if (receivedNewMessages) {
      setHasNewMessages(true);
    }

    previousMessageCountRef.current = messages.length;
  }, [messages, scrollToLatestMessage]);

  useEffect(() => {
    previousMessageCountRef.current = 0;
    isNearBottomRef.current = true;
    setHasNewMessages(false);
  }, [activeConversation?.id]);

  // Obtener información del usuario actual y establecer su rol
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const { uid } = auth.currentUser;
        const userDoc = await getDoc(doc(db, "users", uid));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUserRole({
            isMaster: userData.isMaster === true,
            teacher: userData.teacher === true,
            teacherId: userData.teacherId || null,
            uid: uid,
            displayName: userData.displayName || userData.name || userData.nombre || userData.email || auth.currentUser?.email || "Usuario",
            // Si no es maestro ni profesor, es alumno
            isStudent: !userData.isMaster && !userData.teacher
          });
        }
      } catch (error) {
        console.error("Error al obtener el rol del usuario:", error);
      }
    };
    
    fetchUserRole();
  }, [auth, db]);

  // Publicar actividad del chat para mostrar presencia y última conexión.
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return undefined;

    const userRef = doc(db, "users", uid);
    const updatePresence = (online) => setDoc(userRef, {
      chatPresence: {
        online,
        lastSeenAt: serverTimestamp(),
      },
    }, { merge: true }).catch((error) => {
      console.error("No se pudo actualizar la presencia del chat:", error);
    });

    const handleVisibilityChange = () => updatePresence(!document.hidden);
    const handlePageHide = () => updatePresence(false);

    updatePresence(!document.hidden);
    const heartbeatId = window.setInterval(() => {
      if (!document.hidden) updatePresence(true);
    }, 60000);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      window.clearInterval(heartbeatId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
      updatePresence(false);
    };
  }, [auth, db]);

  // Obtener contactos válidos desde la base de datos
  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) return undefined;

    const unsubscribe = onSnapshot(collection(db, "users"), (usersSnapshot) => {
      const currentUserDoc = usersSnapshot.docs.find((userDoc) => userDoc.id === uid);
      if (!currentUserDoc) return;

      const userData = currentUserDoc.data();
      const allUsersData = usersSnapshot.docs
        .map((userDoc) => ({ id: userDoc.id, ...userDoc.data() }))
        .filter((user) => user.id !== uid);

      setAllUsers(allUsersData);

      let validContacts = [];
      if (userData.isMaster) {
        validContacts = allUsersData;
      } else if (userData.teacher) {
        validContacts = allUsersData.filter((user) => user.isMaster || user.teacherId === uid);
      } else if (userData.teacherId) {
        const assignedTeacher = allUsersData.find((user) => user.id === userData.teacherId);
        validContacts = assignedTeacher ? [assignedTeacher] : [];
      }

      setContacts(validContacts);
    }, (error) => {
      console.error("Error al obtener los contactos:", error);
    });

    return () => unsubscribe();
  }, [auth, db]);

  // Obtener conversaciones desde Firestore
  useEffect(() => {
    const fetchConversations = () => {
      const { uid } = auth.currentUser;
      const q = query(collection(db, "conversations"), where("participants", "array-contains", uid), orderBy("timestamp", "desc"));

      const unsubscribe = onSnapshot(q, async (snapshot) => {
        const conversationList = await Promise.all(
          snapshot.docs.map(async (docSnap) => {
            const conversationData = docSnap.data();
            const contactId = conversationData.participants.find((participant) => participant !== uid);
            const contactDoc = await getDoc(doc(db, "users", contactId));
            
            // Obtener el último mensaje para verificar si hay mensajes no leídos
            const messagesQuery = query(
              collection(db, "conversations", docSnap.id, "messages"),
              orderBy("timestamp", "desc"),
              limit(20)
            );
            
            const messagesSnapshot = await getDocs(messagesQuery);
            const messages = messagesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            
            // Contar mensajes no leídos (mensajes que el usuario actual no ha enviado y no están marcados como leídos)
            const unreadCount = messages.filter(msg => 
              msg.senderId !== uid && 
              (!msg.readBy || !msg.readBy.includes(uid))
            ).length;
            
            return {
              id: docSnap.id,
              ...conversationData,
              contact: contactDoc.exists() ? contactDoc.data() : null,
              contactId,
              unreadCount
            };
          })
        );
        
        setConversations(conversationList);
        
        // Actualizar el estado de mensajes no leídos
        const unreadMap = {};
        conversationList.forEach(conv => {
          if (conv.unreadCount > 0) {
            unreadMap[conv.id] = conv.unreadCount;
          }
        });
        setUnreadMessages(unreadMap);
      });

      return () => unsubscribe();
    };

    fetchConversations();
  }, [auth, db]);

  // Obtener mensajes de la conversación activa
  useEffect(() => {
    if (!activeConversation) return;

    const q = query(collection(db, "conversations", activeConversation.id, "messages"), orderBy("timestamp", "asc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
      setMessages(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    });

    return () => unsubscribe();
  }, [activeConversation, db]);

  // Filtrar contactos según la pestaña seleccionada
  const getFilteredContacts = () => {
    // Para usuarios administradores (isMaster)
    if (userRole.isMaster) {
      if (activeContactTab === "all") {
        return contacts;
      } else if (activeContactTab === "teachers") {
        return allUsers.filter(user => user.teacher === true);
      } else if (activeContactTab === "students") {
        return allUsers.filter(user => !user.teacher && !user.isMaster);
      }
    }
    
    // Para usuarios profesores (teacher)
    if (userRole.teacher) {
      if (activeContactTab === "all") {
        return contacts;
      } else if (activeContactTab === "admins") {
        return allUsers.filter(user => user.isMaster === true);
      } else if (activeContactTab === "students") {
        return allUsers.filter(user => user.teacherId === userRole.uid);
      }
    }
    
    // Para estudiantes - no es necesario filtrar ya que solo muestran a su profesor
    return contacts;
  };

  // Marcar mensajes como leídos al abrir una conversación
  const markMessagesAsRead = useCallback(async (conversationId) => {
    if (!conversationId) return;
    
    const { uid } = auth.currentUser;
    const messagesRef = collection(db, "conversations", conversationId, "messages");
    const q = query(messagesRef, where("senderId", "!=", uid));
    
    const unreadMessages = await getDocs(q);
    const batch = writeBatch(db);
    
    let markedAnyMessage = false;
    unreadMessages.docs.forEach(doc => {
      const messageData = doc.data();
      const readBy = messageData.readBy || [];
      
      if (!readBy.includes(uid)) {
        markedAnyMessage = true;
        batch.update(doc.ref, {
          readBy: [...readBy, uid]
        });
      }
    });
    
    if (markedAnyMessage) {
      await batch.commit();
      
      // Actualizar el contador local
      setUnreadMessages(prev => {
        const updated = { ...prev };
        delete updated[conversationId];
        return updated;
      });
    }

    try {
      await updateDoc(doc(db, "conversations", conversationId), {
        unreadBy: arrayRemove(uid)
      });
    } catch (error) {
      console.error("Error al limpiar notificación de chat:", error);
    }
  }, [auth, db]);

  // Si el chat está abierto, los nuevos mensajes entrantes quedan marcados como vistos.
  useEffect(() => {
    if (!activeConversation?.id || messages.length === 0) return;
    markMessagesAsRead(activeConversation.id);
  }, [activeConversation?.id, markMessagesAsRead, messages]);

  const updateConversationSummary = useCallback(async (conversation, messagePreview, messageType) => {
    if (!conversation?.id) return;

    const { uid } = auth.currentUser;
    const participants = Array.isArray(conversation.participants)
      ? conversation.participants
      : [uid, conversation.contact?.id].filter(Boolean);
    const recipients = participants.filter((participant) => participant !== uid);
    const senderName = userRole.displayName || auth.currentUser?.displayName || auth.currentUser?.email || "Usuario";

    await updateDoc(doc(db, "conversations", conversation.id), {
      lastMessage: messagePreview,
      lastMessageType: messageType,
      lastSenderId: uid,
      lastSenderName: senderName,
      timestamp: serverTimestamp(),
      unreadBy: recipients,
    });
  }, [auth, db, userRole.displayName]);

  useEffect(() => {
    const openConversationFromUrl = async () => {
      if (!selectedConversationId || selectedConversationId === openedConversationId || !userRole.uid) return;

      try {
        const conversationRef = doc(db, "conversations", selectedConversationId);
        const conversationDoc = await getDoc(conversationRef);

        if (!conversationDoc.exists()) return;

        const conversationData = conversationDoc.data();
        const participants = Array.isArray(conversationData.participants)
          ? conversationData.participants
          : [];

        if (!participants.includes(userRole.uid)) return;

        const contactId = participants.find((participant) => participant !== userRole.uid);
        if (!contactId) return;

        const contactDoc = await getDoc(doc(db, "users", contactId));
        if (!contactDoc.exists()) return;

        setActiveConversation({
          id: selectedConversationId,
          ...conversationData,
          contact: { id: contactId, ...contactDoc.data() },
          contactId,
        });
        setOpenedConversationId(selectedConversationId);

        if (isMobileView) {
          setShowChatList(false);
        }

        await markMessagesAsRead(selectedConversationId);
      } catch (error) {
        console.error("Error al abrir conversación desde notificación:", error);
      }
    };

    openConversationFromUrl();
  }, [selectedConversationId, openedConversationId, userRole.uid, db, isMobileView, markMessagesAsRead]);

  // Iniciar una conversación
  const startConversation = async (contact) => {
    const { uid } = auth.currentUser;
    const conversationId = uid > contact.id ? `${uid}_${contact.id}` : `${contact.id}_${uid}`;
    const conversationRef = doc(db, "conversations", conversationId);
    const conversationDoc = await getDoc(conversationRef);

    const conversationData = conversationDoc.exists() ? conversationDoc.data() : {};
    const participants = Array.isArray(conversationData.participants)
      ? conversationData.participants
      : [uid, contact.id];

    if (!conversationDoc.exists()) {
      await setDoc(conversationRef, {
        participants,
        timestamp: serverTimestamp(),
      });
    }

    setActiveConversation({ id: conversationId, ...conversationData, participants, contact });
    setSearchParams({ conversationId });
    
    // En vista mobile/tablet, ocultar la lista de chats al seleccionar uno
    if (isMobileView) {
      setShowChatList(false);
    }
    
    // Marcar mensajes como leídos al abrir la conversación
    markMessagesAsRead(conversationId);
  };

  // Función para volver a la lista de chats (solo mobile/tablet)
  const backToChatList = () => {
    if (isMobileView) {
      setShowChatList(true);
      setActiveConversation(null);
    }
  };

  // Verificar si se puede responder al contacto actual (para estudiantes)
  const canReplyToContact = () => {
    if (!activeConversation) return false;
    
    // Administradores y profesores pueden responder a todos
    if (userRole.isMaster || userRole.teacher) return true;
    
    // Estudiantes solo pueden responder a su profesor asignado
    if (userRole.isStudent && userRole.teacherId) {
      return activeConversation.contact.id === userRole.teacherId;
    }
    
    return true;
  };
  
  // Identificar si una conversación es de solo lectura
  const isReadOnlyConversation = () => {
    // Los estudiantes ya no deberían tener conversaciones de solo lectura
    // porque solo ven a su profesor
    return false;
  };

  // Enviar mensaje de texto
  const sendMessage = async (e) => {
    e.preventDefault();
    if (newMessage.trim() === "" || !activeConversation) return;
    
    // Verificar si se puede responder al contacto actual
    if (!canReplyToContact()) {
      setFileError("No puedes responder a este contacto");
      return;
    }

    const { uid } = auth.currentUser;
    const messageText = newMessage.trim();
    await addDoc(collection(db, "conversations", activeConversation.id, "messages"), {
      senderId: uid,
      text: messageText,
      timestamp: serverTimestamp(),
      readBy: [uid],
    });
    await updateConversationSummary(activeConversation, messageText, "text");

    setNewMessage("");
  };

  // Función para iniciar y detener grabación
  const toggleRecording = async () => {
    if (recording) {
      // Si ya está grabando, detener la grabación
      mediaRecorder.stop();
      setRecording(false);
    } else {
      try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      let audioChunks = [];
      let startTime = Date.now();

      recorder.ondataavailable = (event) => {
        audioChunks.push(event.data);
      };

      recorder.onstop = async () => {
        const duration = ((Date.now() - startTime) / 1000).toFixed(1); // Duración en segundos
        setAudioDuration(duration);

        const audioBlob = new Blob(audioChunks, { type: "audio/webm" });
          
          // Verificar tamaño
          if (audioBlob.size > MAX_FILE_SIZE) {
            setFileError("El archivo de audio excede el límite de 1MB");
            return;
          }
          
          setUploading(true);
        const fileName = `voice_${Date.now()}.webm`;
        const storageRef = ref(storage, `voice_messages/${fileName}`);
        const uploadTask = uploadBytesResumable(storageRef, audioBlob);

        uploadTask.on(
          "state_changed",
            (snapshot) => {
              const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress);
            },
            (error) => {
              console.error("Error al subir el audio:", error);
              setFileError("Error al subir el audio");
              setUploading(false);
            },
          async () => {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            if (activeConversation) {
              const { uid } = auth.currentUser;
              await addDoc(collection(db, "conversations", activeConversation.id, "messages"), {
                senderId: uid,
                audioUrl: downloadURL,
                duration,
                timestamp: serverTimestamp(),
                readBy: [uid],
              });
              await updateConversationSummary(activeConversation, "Mensaje de voz", "audio");
            }
              setUploading(false);
              setUploadProgress(0);
          }
        );
          
          // Detener todos los tracks después de la grabación
          stream.getTracks().forEach(track => track.stop());
      };

      recorder.start();
      setMediaRecorder(recorder);
      setRecording(true);
      } catch (error) {
        console.error("Error al acceder al micrófono:", error);
        setFileError("No se pudo acceder al micrófono");
      }
    }
  };

  // Función para subir archivo multimedia
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const attachmentKind = attachmentKindRef.current;
    const validationError = validateChatAttachment(file, attachmentKind);
    event.target.value = "";
    if (validationError) {
      setFileError(validationError);
      return;
    }
    
    setFileError("");
    setUploading(true);
    
    const fileType = attachmentKind === "image" ? "image" : "document";
    const fileExtension = getFileExtension(file.name);
    const fileName = `${fileType}_${Date.now()}_${sanitizeChatFileName(file.name)}`;
    const storageRef = ref(storage, `media_files/${fileName}`);
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: file.type || "application/octet-stream",
      customMetadata: {
        originalName: file.name,
        extension: fileExtension,
      },
    });

      uploadTask.on(
        "state_changed",
      (snapshot) => {
        const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        setUploadProgress(progress);
      },
      (error) => {
        console.error("Error al subir el archivo:", error);
        setFileError("Error al subir el archivo");
        setUploading(false);
        setUploadProgress(0);
      },
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            if (activeConversation) {
              const { uid } = auth.currentUser;
              await addDoc(collection(db, "conversations", activeConversation.id, "messages"), {
                senderId: uid,
                fileUrl: downloadURL,
                fileName: file.name,
                fileType,
                fileExtension,
                fileMimeType: file.type || "application/octet-stream",
                fileSize: file.size,
              timestamp: serverTimestamp(),
              readBy: [uid],
              });
              await updateConversationSummary(
                activeConversation,
                fileType === "image" ? "Imagen" : file.name,
                fileType === "image" ? "image" : "file"
              );
            }
          } catch (error) {
            console.error("Error al guardar el archivo en el chat:", error);
            setFileError("El archivo se subió, pero no pudo agregarse a la conversación");
          } finally {
            setUploading(false);
            setUploadProgress(0);
            setShowMediaOptions(false);
          }
        }
      );
    };

  // Manejador para mostrar/ocultar opciones multimedia
  const toggleMediaOptions = () => {
    setShowMediaOptions(!showMediaOptions);
  };

  // Clic en botón de imagen
  const handleImageClick = () => {
    attachmentKindRef.current = "image";
    fileInputRef.current.accept = "image/*";
    fileInputRef.current.click();
  };

  // Clic en botón de documento
  const handleFileClick = () => {
    attachmentKindRef.current = "document";
    fileInputRef.current.accept = DOCUMENT_ACCEPT;
    fileInputRef.current.click();
  };

  // Formatear fecha y hora de los mensajes
  const formatTimestamp = (timestamp) => {
    const date = timestampToDate(timestamp);
    if (!date) return "";
    return new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Renderizar pestañas de contactos según el rol del usuario
  const renderContactTabs = () => {
    if (userRole.isMaster) {
      return (
        <Tabs
          activeKey={activeContactTab}
          onSelect={(k) => setActiveContactTab(k)}
          className="contacts-tabs"
        >
          <Tab eventKey="all" title="Todos" />
          <Tab eventKey="teachers" title="Profesores" />
          <Tab eventKey="students" title="Estudiantes" />
        </Tabs>
      );
    } else if (userRole.teacher) {
      return (
        <Tabs
          activeKey={activeContactTab}
          onSelect={(k) => setActiveContactTab(k)}
          className="contacts-tabs"
        >
          <Tab eventKey="all" title="Todos" />
          <Tab eventKey="admins" title="Administradores" />
          <Tab eventKey="students" title="Mis Estudiantes" />
        </Tabs>
      );
    }
    
    // Estudiantes no necesitan pestañas ya que solo ven a su profesor
    return null;
  };

  const conversationsByContact = new Map(conversations.map(conversation => [conversation.contactId, conversation]));
  const normalizedContactSearch = contactSearch.trim().toLocaleLowerCase('es');
  const filteredContacts = getFilteredContacts()
    .filter(contact => {
      if (!normalizedContactSearch) return true;
      const role = contact.isMaster ? 'administrador' : contact.teacher ? 'profesor' : 'estudiante';
      return [contact.username, contact.displayName, contact.name, contact.nombre, contact.email, role]
        .filter(Boolean)
        .some(value => String(value).toLocaleLowerCase('es').includes(normalizedContactSearch));
    })
    .sort((firstContact, secondContact) => {
    const firstConversation = conversationsByContact.get(firstContact.id);
    const secondConversation = conversationsByContact.get(secondContact.id);
    const activityDifference = timestampToMillis(secondConversation?.timestamp) - timestampToMillis(firstConversation?.timestamp);
    if (activityDifference !== 0) return activityDifference;
    return (firstContact.username || firstContact.email || '').localeCompare(
      secondContact.username || secondContact.email || '',
      'es',
      { sensitivity: 'base' }
    );
    });
  const activeContact = activeConversation
    ? allUsers.find((user) => user.id === activeConversation.contactId || user.id === activeConversation.contact?.id) || activeConversation.contact
    : null;
  const canRequestNotifications = notificationPermission === "default";
  const notificationsBlocked = notificationPermission === "denied";
  const canInstallPwa =
    !pwaInstallState.installed && pwaInstallState.promptAvailable;
  const needsManualIosInstall =
    !pwaInstallState.installed && pwaInstallState.iosManualInstall;
  const showEnablementPrompt =
    !enablementDismissed &&
    (
      canRequestNotifications ||
      notificationsBlocked ||
      canInstallPwa ||
      needsManualIosInstall
    );

  return (
    <div className={`chat-container ${userRole.isStudent ? 'student-mode' : ''} ${isMobileView ? 'mobile-view' : ''}`}>
      {showEnablementPrompt && (
        <aside
          className={`chat-enablement ${activeConversation ? "chat-enablement--conversation" : ""}`}
          aria-labelledby="chat-enablement-title"
        >
          <div className="chat-enablement__signal" aria-hidden="true" />
          <div className="chat-enablement__content">
            <div className="chat-enablement__heading">
              <div>
                <span className="chat-enablement__eyebrow">Chat siempre cerca</span>
                <h3 id="chat-enablement-title">No te pierdas un mensaje</h3>
              </div>
              <button
                type="button"
                className="chat-enablement__close"
                onClick={dismissEnablementPrompt}
                aria-label="Cerrar invitación"
              >
                <FaTimes />
              </button>
            </div>

            {canRequestNotifications && (
              <p className="chat-enablement__copy">
                Permite los avisos para enterarte cuando tu profesor o estudiante te escriba.
              </p>
            )}

            {canInstallPwa && (
              <p className="chat-enablement__copy">
                Añade Técnica a tu pantalla de inicio y vuelve al chat en un toque.
              </p>
            )}

            {notificationsBlocked && (
              <p className="chat-enablement__notice">
                Los avisos están bloqueados. Actívalos desde los ajustes del navegador.
              </p>
            )}

            {needsManualIosInstall && (
              <p className="chat-enablement__notice">
                En Safari toca Compartir y luego “Añadir a pantalla de inicio”.
              </p>
            )}

            <div className="chat-enablement__actions">
              {canRequestNotifications && (
                <button
                  type="button"
                  className="chat-enablement__button chat-enablement__button--primary"
                  onClick={enableNotifications}
                  disabled={isRequestingNotifications}
                >
                  <FaBell aria-hidden="true" />
                  <span>
                    {isRequestingNotifications
                      ? "Activando..."
                      : "Activar notificaciones"}
                  </span>
                </button>
              )}

              {canInstallPwa && (
                <button
                  type="button"
                  className="chat-enablement__button chat-enablement__button--secondary"
                  onClick={installPwa}
                  disabled={isInstallingPwa}
                >
                  <FaDownload aria-hidden="true" />
                  <span>
                    {isInstallingPwa
                      ? "Preparando..."
                      : "Instalar aplicación"}
                  </span>
                </button>
              )}
            </div>
          </div>
        </aside>
      )}
      {/* Sidebar de contactos - se oculta en mobile cuando hay conversación activa */}
      {showChatSettings && (
        <>
          <button
            type="button"
            className="chat-settings-backdrop"
            onClick={() => setShowChatSettings(false)}
            aria-label="Cerrar configuración del chat"
          />
          <aside
            className="chat-settings-panel"
            aria-labelledby="chat-settings-title"
          >
            <div className="chat-settings-panel__header">
              <div>
                <span>Preferencias</span>
                <h3 id="chat-settings-title">Configuración del chat</h3>
              </div>
              <button
                type="button"
                onClick={() => setShowChatSettings(false)}
                aria-label="Cerrar configuración del chat"
              >
                <FaTimes />
              </button>
            </div>

            <div className="chat-settings-panel__content">
              <section className="chat-setting">
                <div className="chat-setting__icon" aria-hidden="true">
                  <FaBell />
                </div>
                <div className="chat-setting__body">
                  <div className="chat-setting__title-row">
                    <div>
                      <h4>Notificaciones</h4>
                      <p>
                        {notificationPermission === "granted" && notificationsEnabled
                          ? "Recibirás avisos de mensajes nuevos."
                          : notificationPermission === "denied"
                            ? "El navegador bloqueó este permiso."
                            : notificationPermission === "unsupported"
                              ? "Este navegador no admite avisos."
                              : "Actívalas para recibir avisos del chat."}
                      </p>
                    </div>
                    <button
                      type="button"
                      role="switch"
                      aria-checked={
                        notificationPermission === "granted" && notificationsEnabled
                      }
                      className={`chat-settings-switch ${
                        notificationPermission === "granted" && notificationsEnabled
                          ? "is-active"
                          : ""
                      }`}
                      onClick={handleNotificationsPreference}
                      disabled={
                        notificationPermission === "denied" ||
                        notificationPermission === "unsupported" ||
                        isRequestingNotifications
                      }
                      aria-label={
                        notificationPermission === "granted" && notificationsEnabled
                          ? "Desactivar notificaciones"
                          : "Activar notificaciones"
                      }
                    >
                      <span />
                    </button>
                  </div>

                  {notificationPermission === "denied" && (
                    <p className="chat-setting__notice">
                      Habilita las notificaciones desde los ajustes del navegador o del sistema.
                    </p>
                  )}
                </div>
              </section>

              <section className="chat-setting">
                <div className="chat-setting__icon" aria-hidden="true">
                  <FaDownload />
                </div>
                <div className="chat-setting__body">
                  <h4>Aplicación Técnica</h4>
                  <p>
                    {pwaInstallState.installed
                      ? "Técnica ya funciona como una aplicación independiente."
                      : "Instálala para abrir el chat desde el escritorio o la pantalla de inicio."}
                  </p>

                  {canInstallPwa && (
                    <button
                      type="button"
                      className="chat-setting__install"
                      onClick={installPwa}
                      disabled={isInstallingPwa}
                    >
                      <FaDownload aria-hidden="true" />
                      <span>
                        {isInstallingPwa
                          ? "Preparando instalación..."
                          : "Instalar aplicación"}
                      </span>
                    </button>
                  )}

                  {needsManualIosInstall && (
                    <p className="chat-setting__notice">
                      En Safari toca Compartir y luego “Añadir a pantalla de inicio”.
                    </p>
                  )}

                  {!pwaInstallState.installed &&
                    !canInstallPwa &&
                    !needsManualIosInstall && (
                      <p className="chat-setting__notice">
                        Abre Técnica en Chrome, Edge o Safari para instalarla como aplicación.
                      </p>
                    )}
                </div>
              </section>
            </div>
          </aside>
        </>
      )}
      <div className={`chat-sidebar ${isMobileView && !showChatList ? 'hidden-mobile' : ''}`}>
        <div className="sidebar-header">
          <div className="chat-product-header">
            <div>
              <span className="chat-product-header__eyebrow">Técnica</span>
              <h2>Chat</h2>
            </div>
            <div className="chat-product-header__actions">
              {canInstallPwa && (
                <button
                  type="button"
                  className="chat-feature-button"
                  onClick={installPwa}
                  aria-label="Instalar Técnica como aplicación"
                >
                  <FaDownload />
                  <span>Instalar</span>
                </button>
              )}
              <button
                type="button"
                className="chat-feature-button"
                onClick={openChatSettings}
                aria-label="Configurar chat y notificaciones"
                aria-expanded={showChatSettings}
              >
                <FaCog />
                <span>Ajustes</span>
              </button>
            </div>
          </div>
          <div className="contact-search">
            <FaSearch className="contact-search__icon" aria-hidden="true" />
            <input
              type="search"
              value={contactSearch}
              onChange={event => setContactSearch(event.target.value)}
              placeholder="Buscar personas"
              aria-label="Buscar personas por nombre, correo o rol"
            />
            {contactSearch && (
              <button
                type="button"
                className="contact-search__clear"
                onClick={() => setContactSearch("")}
                aria-label="Limpiar búsqueda"
              >
                <FaTimes aria-hidden="true" />
              </button>
            )}
          </div>
          {renderContactTabs()}
        </div>
        <div className="contacts-list">
          <AnimatePresence>
            {filteredContacts.map((contact) => {
              const contactConversation = conversationsByContact.get(contact.id);
              return (
              <motion.div
                key={contact.id}
                onClick={() => startConversation(contact)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    startConversation(contact);
                  }
                }}
                role="button"
                tabIndex={0}
                className={`contact-item ${activeConversation?.contact?.id === contact.id ? 'active' : ''} 
                  ${contact.isMaster ? 'admin-contact' : contact.teacher ? 'teacher-contact' : 'student-contact'}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <div className="contact-avatar">
                  {(contact.username || contact.email || "").charAt(0).toUpperCase()}
                  
                  {/* Mostrar contador de mensajes no leídos si existen */}
                  {conversations.some(conv => 
                    conv.contactId === contact.id && 
                    conv.unreadCount > 0 &&
                    (!activeConversation || activeConversation.contact.id !== contact.id)
                  ) && (
                    <div className="unread-badge">
                      {conversations.find(conv => conv.contactId === contact.id)?.unreadCount}
                    </div>
                  )}
                </div>
                <div className="contact-info">
                  <div className="contact-summary-row">
                    <span className="contact-name">{contact.username || contact.email}</span>
                    {contactConversation?.timestamp && (
                      <time className="contact-last-time" dateTime={timestampToDate(contactConversation.timestamp)?.toISOString()}>
                        {formatConversationTime(contactConversation.timestamp)}
                      </time>
                    )}
                  </div>
                  {contactConversation?.lastMessage && (
                    <span className="contact-last-message">{contactConversation.lastMessage}</span>
                  )}
                  <div className="contact-details">
                    <span className="contact-status">
                      {contact.isMaster ? "Administrador" : contact.teacher ? "Profesor" : "Estudiante"}
                    </span>
                    <span className={`contact-presence ${formatLastConnection(contact.chatPresence) === "En línea" ? "is-online" : ""}`}>
                      {formatLastConnection(contact.chatPresence)}
                    </span>
                    
                    {/* Mostrar relación profesor-estudiante */}
                    {userRole.teacher && contact.teacherId === userRole.uid && (
                      <span className="contact-relationship">Tu estudiante</span>
                    )}
                    
                    {userRole.isMaster && contact.teacher && (
                      <span className="contact-relationship">Profesor</span>
                    )}
                    
                    {/* Mostrar relación estudiante-profesor */}
                    {userRole.isStudent && contact.id === userRole.teacherId && (
                      <span className="contact-relationship">Tu profesor</span>
                    )}
                  </div>
                </div>
              </motion.div>
              );
            })}
          </AnimatePresence>
          {filteredContacts.length === 0 && (
            <div className="no-contacts-message">
              {normalizedContactSearch
                ? `No se encontraron personas para “${contactSearch.trim()}”`
                : userRole.isStudent && !userRole.teacherId
                ? "No tienes un profesor asignado aún"
                : "No hay contactos disponibles en esta categoría"}
            </div>
          )}
        </div>
      </div>

      {/* Área principal del chat - se muestra siempre en desktop, condicionalmente en mobile */}
      <div className={`chat-main ${isMobileView && showChatList ? 'hidden-mobile' : ''}`}>
        {activeConversation ? (
          <>
            <div className="chat-header">
              {/* Botón de regreso solo en mobile/tablet */}
              {isMobileView && (
                <button className="back-button" onClick={backToChatList} aria-label="Volver a contactos">
                  <FaArrowLeft />
                </button>
              )}
              <div className="contact-avatar active">
                {(activeContact?.username || activeContact?.email || "").charAt(0).toUpperCase()}
              </div>
              <div className="contact-info">
                <h3>
                  {activeContact?.username || activeContact?.email}
                  {isReadOnlyConversation() && (
                    <span className="read-only-badge">Solo lectura</span>
                  )}
                </h3>
                <div className="contact-details">
                  <span className="typing-status">
                    {activeContact?.isMaster
                      ? "Administrador" 
                      : activeContact?.teacher
                        ? "Profesor" 
                        : "Estudiante"}
                  </span>
                  <span className={`contact-presence ${formatLastConnection(activeContact?.chatPresence) === "En línea" ? "is-online" : ""}`}>
                    {formatLastConnection(activeContact?.chatPresence)}
                  </span>
                  
                  {/* Mostrar relación en la conversación activa */}
                  {userRole.teacher && activeContact?.teacherId === userRole.uid && (
                    <span className="contact-relationship">Tu estudiante</span>
                  )}
                  
                  {userRole.isMaster && activeContact?.teacher && (
                    <span className="contact-relationship">Profesor</span>
                  )}
                  
                  {/* Mostrar relación estudiante-profesor */}
                  {userRole.isStudent && activeContact?.id === userRole.teacherId && (
                    <span className="contact-relationship">Tu profesor</span>
                  )}
                </div>
              </div>
              <div className="header-actions">
                <button
                  type="button"
                  className="settings-button settings-button--conversation"
                  onClick={openChatSettings}
                  title="Configurar chat y notificaciones"
                  aria-label="Configurar chat y notificaciones"
                  aria-expanded={showChatSettings}
                >
                  <FaCog />
                </button>
                <button 
                  className="info-button" 
                  onClick={() => setShowUserInfo(!showUserInfo)}
                  title="Ver información del contacto"
                  aria-label="Ver información del contacto"
                  aria-expanded={showUserInfo}
                  aria-controls="chat-contact-info"
                >
                  <FaInfoCircle />
                </button>
              </div>
            </div>
            
            {/* Panel de información del usuario */}
            {showUserInfo && (
              <>
              <button
                type="button"
                className="user-info-backdrop"
                onClick={() => setShowUserInfo(false)}
                aria-label="Cerrar información del contacto"
              />
              <aside
                id="chat-contact-info"
                className="user-info-panel"
                aria-label="Información del contacto"
              >
                <div className="user-info-header">
                  <h4>Información de contacto</h4>
                  <button
                    type="button"
                    onClick={() => setShowUserInfo(false)}
                    aria-label="Cerrar información del contacto"
                  >
                    <FaTimes />
                  </button>
                </div>
                <div className="user-info-content">
                  <div className="info-item">
                    <span className="info-label">Nombre:</span>
                    <span className="info-value">{activeContact?.nombre || activeContact?.username || 'No disponible'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Correo:</span>
                    <span className="info-value">{activeContact?.email || 'No disponible'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Teléfono:</span>
                    <span className="info-value">{activeContact?.telefono || 'No disponible'}</span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">Conexión:</span>
                    <span className="info-value">{formatLastConnection(activeContact?.chatPresence)}</span>
                  </div>
                  {activeContact?.nivel && (
                    <div className="info-item">
                      <span className="info-label">Nivel:</span>
                      <span className="info-value">{activeContact.nivel}</span>
                    </div>
                  )}
                  {activeContact?.matricula && (
                    <div className="info-item">
                      <span className="info-label">Matrícula:</span>
                      <span className="info-value">{activeContact.matricula}</span>
                    </div>
                  )}
                  {activeContact?.institucion && (
                    <div className="info-item">
                      <span className="info-label">Institución:</span>
                    <span className="info-value">{activeContact.institucion}</span>
                  </div>
                  )}
                </div>
              </aside>
              </>
            )}
            
            <div
              ref={chatMessagesRef}
              className="chat-messages"
              onScroll={handleMessagesScroll}
              aria-live="polite"
            >
              {messages.length === 0 && (
                <div className="empty-conversation-history">
                  <div className="empty-conversation-history__icon" aria-hidden="true">
                    <FaComments />
                  </div>
                  <h4>Aún no hay mensajes</h4>
                  <p>Escribe el primer mensaje para iniciar esta conversación.</p>
                </div>
              )}
              <AnimatePresence>
                {messages.map((message, index) => (
                  <React.Fragment key={message.id}>
                  {(index === 0 || !isSameCalendarDay(messages[index - 1]?.timestamp, message.timestamp)) && (
                    <div className="message-date-separator">
                      <time dateTime={timestampToDate(message.timestamp)?.toISOString()}>{formatMessageDate(message.timestamp)}</time>
                    </div>
                  )}
                  <motion.div
                    className={`message-container ${message.senderId === auth.currentUser.uid ? "sent" : "received"} 
                      ${isReadOnlyConversation() && message.senderId !== auth.currentUser.uid ? "read-only-message" : ""}`}
                    initial={{ opacity: 0, scale: 0.8, x: message.senderId === auth.currentUser.uid ? 20 : -20 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.05 }}
                  >
                    <div className="message">
                      {message.text && <ChatMessageText text={message.text} />}
                      
                      {message.audioUrl && (
                        <div className="audio-message">
                          <audio controls src={message.audioUrl}></audio>
                          <span className="audio-duration">{message.duration}s</span>
                        </div>
                      )}
                      
                      {message.fileUrl && message.fileType === 'image' && (
                        <div className="image-message">
                          <img src={message.fileUrl} alt={message.fileName} />
                          <span className="file-name">{message.fileName}</span>
                        </div>
                      )}
                      
                      {message.fileUrl && message.fileType !== 'image' && (
                        <div className="file-message">
                          <FaFile className="file-icon" />
                          <a href={message.fileUrl} target="_blank" rel="noopener noreferrer">{message.fileName}</a>
                        </div>
                      )}
                      
                      <span className="message-meta">
                        <span className="message-time">
                          {message.timestamp && formatTimestamp(message.timestamp)}
                        </span>
                        {message.senderId === auth.currentUser.uid && (
                          <span
                            className={`message-read-receipt ${message.readBy?.some((readerId) => readerId !== auth.currentUser.uid) ? "is-read" : ""}`}
                            aria-label={message.readBy?.some((readerId) => readerId !== auth.currentUser.uid) ? "Visto" : "Enviado"}
                            title={message.readBy?.some((readerId) => readerId !== auth.currentUser.uid) ? "Visto" : "Enviado"}
                          >
                            {message.readBy?.some((readerId) => readerId !== auth.currentUser.uid) ? <FaCheckDouble /> : <FaCheck />}
                            <span>{message.readBy?.some((readerId) => readerId !== auth.currentUser.uid) ? "Visto" : "Enviado"}</span>
                          </span>
                        )}
                      </span>
                    </div>
                  </motion.div>
                  </React.Fragment>
                ))}
              </AnimatePresence>
              <div ref={messagesEndRef} />
            </div>

            {hasNewMessages && (
              <button
                type="button"
                className="new-messages-button"
                onClick={() => scrollToLatestMessage("smooth")}
              >
                Mensajes nuevos
              </button>
            )}
            
            {fileError && (
              <div className="error-message">
                {fileError}
                <button onClick={() => setFileError("")}><FaTimes /></button>
              </div>
            )}
            
            {uploading && (
              <div className="upload-progress">
                <div className="progress-bar">
                  <div className="progress" style={{ width: `${uploadProgress}%` }}></div>
                </div>
                <span>{uploadProgress.toFixed(0)}%</span>
              </div>
            )}
            
            {!isReadOnlyConversation() ? (
            <form onSubmit={sendMessage} className="chat-input">
                <input 
                  type="text" 
                  value={newMessage} 
                  onChange={(e) => setNewMessage(e.target.value)} 
                  placeholder="Escribe un mensaje..." 
                  disabled={uploading || recording}
                  aria-label="Escribe un mensaje"
                />
                
                <div className="chat-actions">
                  <AnimatePresence>
                    {showMediaOptions && (
                      <motion.div
                        className="media-options"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2 }}
                      >
                        <button type="button" onClick={handleImageClick} title="Enviar imagen">
                          <FaImage />
                        </button>
                        <button
                          type="button"
                          onClick={handleFileClick}
                          title="Enviar documento PDF, Word o Excel (máximo 10 MB)"
                          aria-label="Enviar documento PDF, Word o Excel"
                        >
                          <FaFile />
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                  />
                  
                  <button 
                    type="button" 
                    onClick={toggleMediaOptions} 
                    className={`icon-btn ${showMediaOptions ? "active" : ""}`}
                    disabled={uploading || recording}
                    aria-label={showMediaOptions ? "Cerrar opciones de adjuntos" : "Adjuntar imagen o archivo"}
                    aria-expanded={showMediaOptions}
                  >
                    {showMediaOptions ? <FaTimes /> : <FaPaperclip />}
                  </button>
                  
                  <button 
                    type="button" 
                    onClick={toggleRecording} 
                    className={`icon-btn ${recording ? "recording" : ""}`}
                    disabled={uploading}
                    aria-label={recording ? "Detener grabación" : "Grabar nota de voz"}
                  >
                {recording ? <FaStopCircle /> : <FaMicrophone />}
              </button>
                  
                  <button 
                    type="submit" 
                    disabled={newMessage.trim() === "" || uploading || recording}
                    aria-label="Enviar mensaje"
                  >
                    {uploading ? <FaCircleNotch className="fa-spin" /> : <FaPaperPlane />}
                  </button>
                </div>
            </form>
            ) : (
              <div className="read-only-notice">
                <p>Solo puedes ver los mensajes de este administrador</p>
              </div>
            )}
          </>
        ) : (
          <div className="no-conversation">
            <div className="empty-state">
              <div className="empty-icon">💬</div>
              <h3>Selecciona un contacto para iniciar una conversación</h3>
              <p>Tus mensajes aparecerán aquí</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatView;
